import { ImportService } from '@amzn/awscat-portfolio-assessment-service-client';
import { DuplicateSelectionsMap, PropertyConfig } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, SpaceBetween, Spinner } from '@amzn/awsui-components-react';
import React, { PureComponent } from 'react';

import { constants } from '../../../common/Localization';
import { DUPLICATE_SELECT_OPTION, WIZARD_STEP_VALUES } from './common-util/resources';
import { DuplicateRecordsMap, PropertiesConfigMap } from './common-util/types';
import { withWizardContext } from './contexts/WizardContext';
import DuplicateCards from './duplicate-records/DuplicateCards';
import { setDuplicateSelections } from './duplicate-records/DuplicateHelpers';
import QuickSelectPanel from './duplicate-records/QuickSelectPanel';
import { WizardContentProps } from './WizardContentProps';

interface Props extends WizardContentProps, FlashContextInterface {
    duplicateRecordsMap: DuplicateRecordsMap;
}

interface State {
    duplicateSelectionsMap: DuplicateSelectionsMap;
    propertiesConfigMap: PropertiesConfigMap;
}

const stepName: WIZARD_STEP_VALUES = WIZARD_STEP_VALUES.DUPLICATE_RECORDS;
const importService = new ImportService();

/**
 * A container that displays contents for deduplicate step in the wizard
 * Contains a panel container and a Cards component
 */
class DuplicateRecordsStep extends PureComponent<Props, State> {

    state = {
        duplicateSelectionsMap: {} as DuplicateSelectionsMap,
        propertiesConfigMap: new Map<string, PropertyConfig>(),
    };

    async componentDidMount() {
        this.props.setNextAndPrevHandlers(stepName, this.next, this.prev);
        const propertiesConfigMap = this.props.propertiesConfig.reduce((curr: PropertiesConfigMap, property: PropertyConfig)
        : PropertiesConfigMap => {
            curr.set(property.value, property);
            return curr;
        }, new Map<string, PropertyConfig>());
        this.setState({
            propertiesConfigMap,
        });

        const { portfolioId, importId } = this.props.wizardContext;
        try {
            const { duplicateSelectionsMap } = await importService.getDuplicateSelections(portfolioId, importId);
            this.setState({
                duplicateSelectionsMap,
            });
        } catch (err) {
            this.props.flash.alert(FlashType.error, constants.ERR_HEADERS.CANNOT_LOAD_DEDUP_SELECTIONS, '');
        } finally {
            this.props.setLoading(false);
        }
    }

    render() {
        const { duplicateRecordsMap, loading } = this.props;
        if (loading) {
            return <Spinner size='large' />;
        }
        const duplicateCards = (
            <DuplicateCards
                duplicateSelectionsMap={this.state.duplicateSelectionsMap}
                onDuplicateSelectionsChange={this.onDuplicateSelectionsChange}
                duplicateRecordsMap={duplicateRecordsMap}
                propertiesConfigMap={this.state.propertiesConfigMap}
            />
        );

        const quickSelectPanel = (
            <QuickSelectPanel
                onQuickSelectChange={this.onQuickSelectChange}
                duplicateRecordsMap={duplicateRecordsMap}
                duplicateSelectionsMap={this.state.duplicateSelectionsMap}
            />
        );
        return (
            <Box padding={{vertical: 'l'}}>
                <SpaceBetween size='l'>
                    {quickSelectPanel}
                    {duplicateCards}
                </SpaceBetween>
            </Box>
        );
    };

    next = async (): Promise<void> => {
        this.validateSelections();
        const { portfolioId, importId } = this.props.wizardContext;
        await importService.saveDuplicateSelections(portfolioId, importId, this.state.duplicateSelectionsMap);
    };

    prev = async (): Promise<void> => {};

    /**
     * Validate all records have been selected
     */
    validateSelections = (): void => {
        const { duplicateRecordsMap } = this.props;
        const { duplicateSelectionsMap } = this.state;
        const allSelected: boolean = Object.keys(duplicateRecordsMap).every((duplicateRecordId: string) =>
            duplicateSelectionsMap[duplicateRecordId] != null );
        if (!allSelected) {
            throw new Error(constants.ERR_UNFINISHED_DEDUP);
        }
    };

    onDuplicateSelectionsChange = (duplicateSelectionsMap: DuplicateSelectionsMap): void => {
        this.setState({
            duplicateSelectionsMap,
        });
    };

    onQuickSelectChange = (option: DUPLICATE_SELECT_OPTION): void => {
        const duplicateSelectionsMapCopy = {...this.state.duplicateSelectionsMap};
        setDuplicateSelections(duplicateSelectionsMapCopy, this.props.duplicateRecordsMap, option);
        this.setState({
            duplicateSelectionsMap: duplicateSelectionsMapCopy,
        });
    };
}

export default withFlashContext(withWizardContext(DuplicateRecordsStep));
